// Admin sidebar + top header layout
import React from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Building2, Globe, Ruler, LayoutTemplate,
  Megaphone, ImageIcon, BarChart3, LogOut, ShieldAlert, Radio, Users, Activity,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import Footer from "@/components/Footer";

const nav = [
  { to: "/dashboard", label: "Gösterge Paneli", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/municipalities", label: "Belediyeler", icon: Building2, testid: "nav-municipalities" },
  { to: "/publisher-sites", label: "Yayıncı Siteleri", icon: Globe, testid: "nav-sites" },
  { to: "/placements", label: "Reklam Alanları", icon: LayoutTemplate, testid: "nav-placements" },
  { to: "/campaigns", label: "Kampanyalar", icon: Megaphone, testid: "nav-campaigns" },
  { to: "/creatives", label: "Kreatifler", icon: ImageIcon, testid: "nav-creatives" },
  { to: "/live-preview", label: "Canlı Önizleme", icon: Radio, testid: "nav-live-preview" },
  { to: "/ad-sizes", label: "Reklam Ölçüleri", icon: Ruler, testid: "nav-sizes", adminOnly: true },
  { to: "/reports", label: "Raporlar", icon: BarChart3, testid: "nav-reports" },
  { to: "/users", label: "Kullanıcılar", icon: Users, testid: "nav-users", adminOnly: true },
  { to: "/unauthorized", label: "Yetkisiz Domainler", icon: ShieldAlert, testid: "nav-unauthorized" },
  { to: "/system", label: "Sistem Durumu", icon: Activity, testid: "nav-system", adminOnly: true },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isSuper = user?.role === "super_admin";

  return (
    <div className="min-h-screen flex bg-slate-50">
      {/* Sidebar */}
      <aside className="w-64 shrink-0 bg-slate-950 text-slate-300 flex flex-col" data-testid="admin-sidebar">
        <div className="px-5 py-6 border-b border-slate-900">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-sm bg-red-600 flex items-center justify-center text-white font-bold font-heading">T</div>
            <div>
              <div className="font-heading font-bold text-white leading-tight text-[15px]">Türk ADS CDN</div>
              <div className="text-[10.5px] uppercase tracking-[0.15em] text-slate-500">Reklam Dağıtım Ağı</div>
            </div>
          </div>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {nav.filter((n) => !n.adminOnly || isSuper).map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={n.testid}
              className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`}
            >
              <n.icon className="w-4 h-4 shrink-0" />
              <span>{n.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-slate-900">
          <div className="text-[11px] text-slate-500 uppercase tracking-wider mb-2">Hesap</div>
          <div className="text-sm text-slate-200 truncate" data-testid="sidebar-user-email">{user?.email}</div>
          <div className="text-xs text-slate-500 mb-3">{isSuper ? "Süper Yönetici" : "Belediye Kullanıcısı"}</div>
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-start text-slate-300 hover:bg-slate-900 hover:text-white"
            onClick={async () => { await logout(); navigate("/login"); }}
            data-testid="logout-button"
          >
            <LogOut className="w-4 h-4 mr-2" /> Çıkış Yap
          </Button>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 min-w-0">
        <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-8">
          <div>
            <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500">Yönetim Paneli</div>
            <div className="font-heading font-semibold text-slate-950 text-lg leading-tight">Türk ADS CDN</div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-slate-500 hidden md:inline">Aktif oturum:</span>
            <span className="text-sm font-medium text-slate-800" data-testid="header-user-name">{user?.name}</span>
            <span className={`text-[10.5px] uppercase tracking-widest px-2 py-1 rounded-sm ${isSuper ? "bg-red-600 text-white" : "bg-slate-900 text-white"}`}>
              {isSuper ? "Süper Admin" : "Belediye"}
            </span>
          </div>
        </header>
        <div className="p-6 md:p-8" data-testid="page-content">
          <Outlet />
        </div>
        <Footer />
      </main>
    </div>
  );
}
